import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../composants/Button/Button';
import TableauDeBord from '../../../../composants/TableauDeBord/TableauDeBord';
import { formatDate } from '../../../../utils/entityUtils';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';

// ── Barre de progression profil ───────────────────────────────────────────────

export function ProfilProgressBar({ value }: { value: number }) {
  const color = value === 100 ? '#22c55e' : value >= 50 ? '#eab308' : '#ef4444';
  return (
    <div className="profil-progress-wrapper">
      <div className="profil-progress-bar">
        <div
          className="profil-progress-fill"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="profil-progress-label">{value}%</span>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface EntrepriseInfo {
  id: number;
  nom: string;
  siret: string;
  representant?: string;
  email?: string;
  telephone?: string;
  progression: number;
}

interface GroupementMember {
  id: number;
  entreprise: EntrepriseInfo;
  entite_profil?: {
    id: number;
    nom: string;
    siret: string;
    progression: number;
  } | null;
  accepted_at: string | null;
  status: 'pending' | 'accepted' | 'refused';
  // Vrai si cette ligne est l'entreprise qui consulte (fourni par le back).
  is_self?: boolean;
}

interface ConventionDoc {
  id: number;
  filename: string;
  created_at?: string;
}

interface SectionCandidatProps {
  entite: EntrepriseInfo;
  groupementMembers: GroupementMember[];
  estMandataire: boolean;
  isSoumise: boolean;
  onRetirer: (membreId: number, membreNom: string) => void;
  canInvite?: boolean;   // ← AJOUT. Défaut : true
  inviteTooltip?: string; // Tooltip quand canInvite=false (ex: "Indisponible en mode démo")
  onInviter: () => void;
  onViewMemberProfile?: (entiteId: number) => void;
  // Story 31.3 — Convention de groupement
  conventionVierge?: { id: number; filename: string } | null;
  conventionSignee?: ConventionDoc | null;
  onDownloadConventionVierge?: () => void;
  onDownloadConventionSignee?: () => void;
  onUploadConventionSignee?: (file: File) => void;
  onDeleteConventionSignee?: () => void;
  conventionSigneeUploading?: boolean;
}

// ── Section "Candidat" ────────────────────────────────────────────────────────

const SectionCandidat: React.FC<SectionCandidatProps> = ({
  entite,
  groupementMembers,
  estMandataire,
  isSoumise,
  onRetirer,
  canInvite = true,
  inviteTooltip,
  onInviter,
  onViewMemberProfile,
  conventionVierge,
  conventionSignee,
  onDownloadConventionVierge,
  onDownloadConventionSignee,
  onUploadConventionSignee,
  onDeleteConventionSignee,
  conventionSigneeUploading = false,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <div className="section-content">
        {/* Bloc mandataire — disposition horizontale */}
        <div className="mandataire-card">
          <div className="mandataire-info-row">
            <span className="mandataire-name">{entite.nom}</span>
            {entite.siret && <span className="mandataire-field">Siret: {entite.siret}</span>}
            {entite.representant && <span className="mandataire-field">Représentant: {entite.representant}</span>}
            {entite.email && <span className="mandataire-field">Mail: {entite.email}</span>}
            {entite.telephone && <span className="mandataire-field">Tel: {entite.telephone}</span>}
            <ProfilProgressBar value={entite.progression} />
          </div>
          <div className='mandataire-profil-button'>
            {/* Le bloc du haut affiche le mandataire. Seul le mandataire peut ouvrir son profil ;
                pour un cotraitant invite, le bouton est grise (il ne voit pas le profil du mandataire). */}
            <span
              title={!estMandataire ? 'Vous ne pouvez pas consulter le profil du mandataire' : undefined}
              style={{ display: 'inline-block' }}
            >
              <Button
                type="button"
                variant="secondary"
                value="Voir le profil"
                size="small"
                disabled={!estMandataire}
                onClick={estMandataire ? () => navigate('/profil') : undefined}
              />
            </span>
          </div>
        </div>


        {/* Table cotraitants */}
        {groupementMembers.length > 0 && (
          <TableauDeBord
            data={groupementMembers.map((member) => ({
              id: member.id,
              entreprise_id: member.entreprise.id,
              nom: member.entreprise.nom,
              siret: member.entreprise.siret,
              representant: member.entreprise.representant,
              email: member.entreprise.email,
              telephone: member.entreprise.telephone,
              progression: member.entreprise.progression,
              status: member.status,
              accepted_at: member.accepted_at,
              is_self: member.is_self
            }))}
            columns={[
              { field: 'nom', header: 'Nom de l\'entreprise' },
              { field: 'siret', header: 'Siret' },
              { field: 'representant', header: 'Représentant' },
              { field: 'email', header: 'Mail' },
              { field: 'telephone', header: 'Tél' },
              {
                field: 'status',
                header: 'Statut',
                renderCell: (value: string) => (
                  <span className={`member-status member-status--${value}`}>
                    {value === 'accepted' ? 'Accepté' : value === 'pending' ? 'En attente' : value}
                  </span>
                )
              },
              {
                field: 'accepted_at',
                header: 'Accepté le',
                renderCell: (value: string | null | Date) => value ? formatDate(value) : '-'
              },
              {
                field: 'progression',
                header: 'Remplissage du profil',
                renderCell: (value: number) => <ProfilProgressBar value={value} />
              },
              {
                field: 'profile',
                header: 'Voir le profil complet',
                renderCell: (_value: any, item: any) => {
                  // Mandataire : voit le profil de tous les cotraitants invites.
                  if (estMandataire) {
                    return (
                      <Button
                        type="button"
                        variant="secondary"
                        value="Voir le profil"
                        size="small"
                        className='view-profile-button'
                        onClick={() => item.entreprise_id && onViewMemberProfile && onViewMemberProfile(item.entreprise_id)}
                        disabled={!item.entreprise_id}
                      />
                    );
                  }
                  // Cotraitant : le bouton n'apparait que sur sa propre ligne et renvoie vers son profil.
                  if (item.is_self) {
                    return (
                      <Button
                        type="button"
                        variant="secondary"
                        value="Voir le profil"
                        size="small"
                        className='view-profile-button'
                        onClick={() => navigate('/profil')}
                      />
                    );
                  }
                  return null;
                }
              },
              ...(estMandataire ? [{

                field: 'actions',
                header: 'Actions',
                renderCell: (_value: any, item: any) => (
                  <div className="member-actions">

                    {(item.status === 'accepted' || item.status === 'pending') && (

                      <Button
                        type="button"
                        variant="cancel"
                        value=""
                        size="small"
                        className='delete-member-button'
                        icon={<DeleteIcon />}
                        onClick={() => onRetirer(item.entreprise_id, item.nom)}
                      />
                    )}

                  </div>
                )
              }] : [])
            ]}
            selectedItems={[]}
            onSelectionChange={() => { }}
            isSelectable={false}
            showActionsColumn={false}
            showPagination={false}
          />
        )}

        {/* Footer : convention + ajouter membre — même ligne */}
        {groupementMembers.length > 0 && estMandataire && (
          <div className="groupement-footer">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="convention-groupement-file-input"
              disabled={conventionSigneeUploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onUploadConventionSignee) onUploadConventionSignee(file);
                e.target.value = '';
              }}
            />

            {/* Partie gauche : convention */}
            <div className="convention-inline">
              <div className="convention-bloc-label">
                Convention de groupement signée&nbsp;<span className="required-star">*</span>
                {conventionVierge && (
                  <button
                    type="button"
                    className="convention-model-link"
                    onClick={onDownloadConventionVierge}
                  >
                    Télécharger le modèle à signer ↓
                  </button>
                )}
              </div>

              {conventionSignee ? (
                <div className="convention-doc-area">
                  <div
                    className="convention-doc-card"
                    onClick={onDownloadConventionSignee}
                    role="button"
                    title="Prévisualiser la convention signée"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="convention-doc-icon">📄</div>
                    <div className="convention-doc-info">
                      <span className="convention-doc-name">{conventionSignee.filename}</span>
                      {conventionSignee.created_at && (
                        <span className="convention-doc-date">
                          Déposée le {new Date(conventionSignee.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  {!isSoumise && (
                    <Button
                      type="button"
                      variant="secondary"
                      value={conventionSigneeUploading ? 'Envoi…' : 'Mettre à jour'}
                      size="small"
                      disabled={conventionSigneeUploading}
                      onClick={() => fileInputRef.current?.click()}
                    />
                  )}
                </div>
              ) : (
                !isSoumise && (
                  <Button
                    type="button"
                    variant="primary"
                    value={conventionSigneeUploading ? 'Envoi…' : 'Déposer la convention signée'}
                    size="small"
                    disabled={conventionSigneeUploading}
                    onClick={() => fileInputRef.current?.click()}
                  />
                )
              )}
            </div>

            {/* Partie droite : ajouter membre */}
            {!isSoumise && (
              <span title={!canInvite && inviteTooltip ? inviteTooltip : undefined} style={{ display: 'inline-block' }}>
                <Button
                  type="button"
                  variant="primary"
                  value="Ajouter un membre au groupement"
                  size="small"
                  disabled={!canInvite}
                  onClick={canInvite ? onInviter : undefined}
                />
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SectionCandidat;
